import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FolderRail, FolderToolbar, FolderCard, partitionCurrentLevel, useFolderCrud } from "@/components/FolderGrid";
import { Plus, Search, FileText, Trash2, Edit, Eye, EyeOff, Lock, Users, X, Upload, Wand2 } from "lucide-react";
import TierGate from "@/components/TierGate";
import EntityContextMenu from "@/components/EntityContextMenu";
import { FolderColorStrip } from "@/components/FolderColorStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HandoutsPage = () => {
  const { campaignId } = useParams();
  const [handouts, setHandouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHandout, setEditingHandout] = useState(null);
  const [selectedHandout, setSelectedHandout] = useState(null);
  const [folders, setFolders] = useState([]);
  const folderCrud = useFolderCrud({ entityType: "handouts", onRefresh: () => fetchHandouts() });
  useEffect(() => { setFolders(folderCrud.folders); }, [folderCrud.folders]);
  const [formData, setFormData] = useState({ name: "", content: "", permissions: "dm_only", folder_id: null });
  const [imageFile, setImageFile] = useState(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    fetchHandouts();
  }, [campaignId]);

  const fetchHandouts = async () => {
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/handouts`, { withCredentials: true });
      setHandouts(res.data);
      folderCrud.refresh();
    } catch { toast.error("Failed to load handouts"); }
    setLoading(false);
  };

  const createHandout = async () => {
    if (!formData.name.trim()) { toast.error("Name is required"); return; }
    try {
      const body = { ...formData, image_url: null };
      const res = await axios.post(`${API}/campaigns/${campaignId}/handouts`, body, { withCredentials: true });
      // Upload image separately if provided
      if (imageFile && res.data?.id) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const upRes = await axios.post(`${API}/handouts/${res.data.id}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        res.data.image_url = upRes.data.image_url;
      }
      setHandouts(p => [...p, res.data]);
      setDialogOpen(false);
      resetForm();
      toast.success("Handout created");
    } catch { toast.error("Failed to create handout"); }
  };

  const updateHandout = async () => {
    if (!editingHandout) return;
    try {
      let updateBody = { ...formData };
      await axios.put(`${API}/handouts/${editingHandout.id}`, updateBody, { withCredentials: true });
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await axios.post(`${API}/handouts/${editingHandout.id}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      }
      fetchHandouts();
      setDialogOpen(false);
      resetForm();
      toast.success("Handout updated");
    } catch { toast.error("Failed to update handout"); }
  };

  const deleteHandout = async (id) => {
    if (!window.confirm("Delete this handout?")) return;
    try {
      await axios.delete(`${API}/handouts/${id}`, { withCredentials: true });
      setHandouts(p => p.filter(h => h.id !== id));
      if (selectedHandout?.id === id) setSelectedHandout(null);
      toast.success("Handout deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const togglePermission = async (handout, perm) => {
    try {
      await axios.put(`${API}/handouts/${handout.id}`, { permissions: perm }, { withCredentials: true });
      setHandouts(p => p.map(h => h.id === handout.id ? { ...h, permissions: perm } : h));
      toast.success(`Handout ${perm === "dm_only" ? "hidden from" : "shared with"} players`);
    } catch { toast.error("Failed to update"); }
  };

  const moveToFolder = async (handoutId, folderId) => {
    try {
      await axios.put(`${API}/campaigns/${campaignId}/handouts/${handoutId}/folder`, { folder_id: folderId }, { withCredentials: true });
      setHandouts(p => p.map(h => h.id === handoutId ? { ...h, folder_id: folderId } : h));
    } catch { toast.error("Failed to move"); }
  };

  const resetForm = () => {
    setFormData({ name: "", content: "", permissions: "dm_only", folder_id: null });
    setEditingHandout(null);
    setImageFile(null);
  };

  const generateImage = async (handoutId) => {
    const h = handouts.find(x => x.id === handoutId);
    if (!h) return;
    try {
      toast.info("Generating AI image...");
      await axios.post(`${API}/handouts/${handoutId}/generate-image`, { prompt: `D&D fantasy illustration for handout: ${h.name}. ${(h.content || "").substring(0, 200)}. Dark fantasy style, atmospheric, detailed, no text` }, { withCredentials: true });
      toast.success("Image generation started — refresh in a moment");
      setTimeout(fetchHandouts, 5000);
    } catch { toast.error("Failed to generate image"); }
  };

  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const createWithAI = async () => {
    if (!aiPrompt.trim()) { toast.error("Enter a description"); return; }
    setAiGenerating(true);
    try {
      // Create handout with AI-generated name and content
      toast.info("Creating handout with AI...");
      const res = await axios.post(`${API}/campaigns/${campaignId}/handouts`, { name: aiPrompt.substring(0, 50), content: aiPrompt, permissions: "dm_only" }, { withCredentials: true });
      // Generate image
      await axios.post(`${API}/handouts/${res.data.id}/generate-image`, { prompt: `D&D fantasy illustration, ${aiPrompt.substring(0, 200)}, dark fantasy, atmospheric, no text` }, { withCredentials: true });
      setHandouts(p => [...p, res.data]);
      setAiPrompt("");
      toast.success("Handout created! Image generating...");
      setTimeout(fetchHandouts, 5000);
    } catch { toast.error("Failed to create with AI"); }
    setAiGenerating(false);
  };

  const openEdit = (h) => {
    setFormData({ name: h.name, content: h.content, permissions: h.permissions, folder_id: h.folder_id });
    setEditingHandout(h);
    setDialogOpen(true);
  };

  const filtered = handouts.filter(h => h.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredAll = filtered;
  const { folders: levelFolders, items: filteredByFolder } = partitionCurrentLevel({
    folders, items: filteredAll, currentFolderId: folderCrud.currentFolderId
  });

  const permIcon = (p) => p === "dm_only" ? <Lock className="w-3 h-3 text-red-400" /> : p === "visible" ? <Eye className="w-3 h-3 text-green-400" /> : <Edit className="w-3 h-3 text-blue-400" />;
  const permLabel = (p) => p === "dm_only" ? "DM Only" : p === "visible" ? "Visible to Players" : "Editable by Players";

  if (loading) return <Layout campaignName="Loading..."><div className="flex items-center justify-center h-64"><div className="spinner" /></div></Layout>;

  return (
    <Layout campaignName="Handouts">
      <div className="w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-serif text-foreground">Handouts</h1>
            <p className="text-muted-foreground text-sm mt-1">Share notes, maps, and lore with your players</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary text-primary-foreground rounded-sm" data-testid="create-handout-btn">
              <Plus className="w-4 h-4 mr-2" /> New Handout
            </Button>
          </div>
        </div>

        {/* AI Create Handout */}
        <div className="mb-4 flex gap-2 items-center" data-testid="ai-handout-create">
          <div className="relative flex-1">
            <Wand2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
            <Input value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder="Describe a handout and AI will create it with an image..." className="pl-10 bg-input border-border rounded-sm" onKeyDown={e => e.key === "Enter" && createWithAI()} data-testid="ai-handout-prompt" />
          </div>
          <Button onClick={createWithAI} disabled={aiGenerating} className="bg-primary/80 text-primary-foreground rounded-sm" data-testid="ai-handout-submit">
            <Wand2 className="w-4 h-4 mr-1" /> {aiGenerating ? "Creating..." : "Create with AI"}
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search handouts..." className="pl-10 bg-input border-border rounded-sm" data-testid="handout-search" />
          </div>
        </div>

        <div className="flex gap-4">
          <FolderRail folders={folders} items={handouts} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} allLabel="All Handouts" className="w-44 shrink-0 sticky top-2 self-start" testIdPrefix="handouts" />
          {/* List */}
          <div className="flex-1 min-w-0">
            <FolderToolbar folders={folders} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} onNewFolder={folderCrud.createHere} allLabel="All Handouts" className="mb-3" />
            {levelFolders.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 mb-4" data-testid="handouts-folder-grid">
                {levelFolders.map(f => (
                  <FolderCard key={f.id} folder={f}
                    count={handouts.filter(h => h.folder_id === f.id).length + folders.filter(x => x.parent_id === f.id && !x.archived).length}
                    onOpen={() => folderCrud.setCurrentFolderId(f.id)}
                    onRename={() => folderCrud.renameFolder(f)}
                    onDelete={() => folderCrud.deleteFolder(f)}
                    onArchive={() => folderCrud.toggleArchive(f)}
                    onColor={(c) => folderCrud.setFolderColor(f, c)}
                    onDropItem={folderCrud.moveItem} />
                ))}
              </div>
            )}
            {filteredByFolder.length === 0 && levelFolders.length === 0 ? (
              <div className="text-center py-16">
                <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-serif text-foreground mb-2">{handouts.length === 0 ? "No handouts yet" : "No matches"}</h3>
                <p className="text-muted-foreground mb-6">{handouts.length === 0 ? "Create handouts to share with your party" : "Try different search"}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                {filteredByFolder.map(h => (
                  <EntityContextMenu
                    key={h.id}
                    onOpen={() => setSelectedHandout(h)}
                    onRename={() => openEdit(h)}
                    onDelete={() => deleteHandout(h.id)}
                    folders={folders}
                    currentFolderId={h.folder_id}
                    onMoveFolder={(fid) => moveToFolder(h.id, fid)}
                    pinPath="handouts"
                    pinLabel={`Handout: ${h.name}`}
                    entityName={h.name}
                  >
                  <Card className="bg-card border-border rounded-sm cursor-pointer hover:border-primary/40 transition-colors overflow-hidden relative" onClick={() => setSelectedHandout(h)} draggable onDragStart={e => e.dataTransfer.setData("entityId", h.id)} data-testid={`handout-card-${h.id}`}>
                    <FolderColorStrip entityId={h.id} folderId={h.folder_id} folders={folders} onFilter={folderCrud.setCurrentFolderId} testIdPrefix="handout" />
                    {h.image_url && (
                      <div className="h-28 overflow-hidden">
                        <img src={h.image_url} alt={h.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-medium text-foreground truncate">{h.name}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{h.content ? h.content.substring(0, 100) + (h.content.length > 100 ? "..." : "") : "Empty handout"}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {permIcon(h.permissions)}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                        <span className="text-[10px] text-muted-foreground">{permLabel(h.permissions)}</span>
                        <div className="flex gap-1">
                          <button className="p-1 hover:text-primary" onClick={e => { e.stopPropagation(); openEdit(h); }} data-testid={`edit-handout-${h.id}`}><Edit className="w-3 h-3" /></button>
                          <button className="p-1 hover:text-primary" onClick={e => { e.stopPropagation(); togglePermission(h, h.permissions === "dm_only" ? "visible" : "dm_only"); }} title={h.permissions === "dm_only" ? "Share with players" : "Hide from players"} data-testid={`toggle-perm-${h.id}`}>
                            {h.permissions === "dm_only" ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          </button>
                          <button className="p-1 hover:text-destructive" onClick={e => { e.stopPropagation(); deleteHandout(h.id); }} data-testid={`delete-handout-${h.id}`}><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  </EntityContextMenu>
                ))}
              </div>
            )}
          </div>

          {/* Detail panel */}
          {selectedHandout && (
            <div className="w-96 bg-card border border-border rounded-sm flex-shrink-0 overflow-hidden" data-testid="handout-detail">
              <div className="flex items-center justify-between p-3 border-b border-border">
                <h3 className="text-base font-serif text-foreground truncate">{selectedHandout.name}</h3>
                <button onClick={() => setSelectedHandout(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
              </div>
              {selectedHandout.image_url && (
                <div className="h-40 overflow-hidden">
                  <img src={selectedHandout.image_url} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-3 overflow-y-auto max-h-[50vh]">
                <div className="flex items-center gap-2 mb-3">
                  {permIcon(selectedHandout.permissions)}
                  <span className="text-xs text-muted-foreground">{permLabel(selectedHandout.permissions)}</span>
                </div>
                <div className="prose prose-sm prose-invert max-w-none">
                  {selectedHandout.content ? (
                    <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{selectedHandout.content}</div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No content yet</p>
                  )}
                </div>
              </div>
              <div className="p-3 border-t border-border flex gap-2">
                <Button size="sm" variant="outline" className="flex-1 rounded-sm text-xs" onClick={() => openEdit(selectedHandout)}>
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => generateImage(selectedHandout.id)}>
                  <Wand2 className="w-3 h-3 mr-1" /> AI Image
                </Button>
                <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => togglePermission(selectedHandout, selectedHandout.permissions === "dm_only" ? "visible" : selectedHandout.permissions === "visible" ? "editable" : "dm_only")}>
                  {selectedHandout.permissions === "dm_only" ? <><Eye className="w-3 h-3 mr-1" /> Share</> : selectedHandout.permissions === "visible" ? <><Edit className="w-3 h-3 mr-1" /> Editable</> : <><Lock className="w-3 h-3 mr-1" /> DM Only</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={v => { if (!v) resetForm(); setDialogOpen(v); }}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif">{editingHandout ? "Edit Handout" : "New Handout"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium block mb-1">Name</label>
              <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Handout title..." className="bg-input border-border rounded-sm" data-testid="handout-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Content</label>
              <textarea value={formData.content} onChange={e => setFormData(p => ({ ...p, content: e.target.value }))} placeholder="Write the handout content..." className="w-full h-40 bg-input border border-border rounded-sm p-3 text-sm resize-none text-foreground" data-testid="handout-content-input" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Image (optional)</label>
              <input ref={imageInputRef} type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} className="hidden" />
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => imageInputRef.current?.click()}>
                <Upload className="w-3 h-3 mr-1" /> {imageFile ? imageFile.name : "Upload Image"}
              </Button>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Permissions</label>
              <Select value={formData.permissions} onValueChange={v => setFormData(p => ({ ...p, permissions: v }))}>
                <SelectTrigger className="bg-input border-border rounded-sm" data-testid="handout-perm-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dm_only"><div className="flex items-center gap-2"><Lock className="w-3 h-3 text-red-400" /> DM Only</div></SelectItem>
                  <SelectItem value="visible"><div className="flex items-center gap-2"><Eye className="w-3 h-3 text-green-400" /> Visible to Players</div></SelectItem>
                  <SelectItem value="editable"><div className="flex items-center gap-2"><Edit className="w-3 h-3 text-blue-400" /> Editable by Players</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
            {folders.length > 0 && (
              <div>
                <label className="text-sm font-medium block mb-1">Folder</label>
                <Select value={formData.folder_id || "none"} onValueChange={v => setFormData(p => ({ ...p, folder_id: v === "none" ? null : v }))}>
                  <SelectTrigger className="bg-input border-border rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No folder</SelectItem>
                    {folders.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={editingHandout ? updateHandout : createHandout} className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="submit-handout-btn">
              {editingHandout ? "Update Handout" : "Create Handout"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default HandoutsPage;
